import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Turnstile from 'react-turnstile';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [isVerified, setIsVerified] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'お問い合わせ | パタカラ®公式施設ライセンス',
        description:
          'パタカラ®公式施設ライセンスに関するお問い合わせはこちら。介護施設・医療施設向けライセンスサービスについてご不明な点はお気軽にご連絡ください。',
        url: `${siteUrl}/contact`,
        inLanguage: 'ja',
        isPartOf: {
          '@type': 'WebSite',
          name: 'パタカラ®公式施設ライセンス',
          url: `${siteUrl}/`,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'お問い合わせ | パタカラ®公式施設ライセンス',
        url: `${siteUrl}/contact`,
        description: 'パタカラ®公式施設ライセンスに関するお問い合わせフォーム。',
        mainEntity: {
          '@type': 'Organization',
          name: '株式会社パックスシステムズ',
          telephone: '045-681-3165',
          email: 'info@patakara.org',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '山下町1番地 シルクセンターM1F',
            addressLocality: '横浜市中区',
            addressRegion: '神奈川県',
            postalCode: '231-0023',
            addressCountry: 'JP',
          },
        },
      },
    ];

    const existing = document.querySelectorAll('script[data-schema="contact"]');
    existing.forEach((s) => s.remove());

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'contact');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="contact"]').forEach((s) => s.remove());
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isVerified || !turnstileToken) {
      alert('「私はロボットではありません」の確認をしてください。');
      return;
    }

    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement | null;

    if (textarea && textarea.value.length > 500) {
      alert('お問い合わせ内容は500文字以内でご入力ください。');
      return;
    }

    setSubmitting(true);

    const formData = new FormData(form);
    formData.append('cf-turnstile-response', turnstileToken);

    try {
      const response = await fetch('/api/send.php', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.ok) {
        setSubmitted(true);
        form.reset();
        setCharCount(0);
        setIsVerified(false);
        setTurnstileToken('');
      } else {
        alert(data.message || '送信に失敗しました。');
      }
    } catch (error) {
      alert('送信中にエラーが発生しました。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/1827e98d95d9f7558266c3fb82d75725/927c3b547f45ed1f38c15098c60865bf.png"
                alt="パタカラ®ロゴ"
                className="h-12 w-auto object-contain"
              />
            </Link>
          </div>
          <div className="flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors">
              ホーム
            </Link>
            <Link to="/terms" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors">
              利用規約
            </Link>
            <Link
              to="/contact"
              className="px-6 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition-all whitespace-nowrap cursor-pointer"
            >
              お問い合わせ
            </Link>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-12 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 mb-6 shadow-lg">
            <i className="ri-mail-line text-white text-2xl"></i>
          </div>
          <h1 className="text-4xl font-serif font-black text-slate-800 mb-4">お問い合わせ</h1>
          <p className="text-base text-gray-600 leading-relaxed">
            ご不明な点やご質問がございましたら、下記フォームよりお気軽にお問い合わせください。
            <br />
            担当者より1〜2営業日以内にご返信いたします。
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6">
          {submitted ? (
            <div className="text-center py-20 animate-fade-in">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-green-500 text-4xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3">送信が完了しました</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                お問い合わせありがとうございます。
                <br />
                担当者より1〜2営業日以内にご連絡いたします。
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line"></i>
                トップページへ戻る
              </Link>
            </div>
          ) : (
            <form
              id="contact-form"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 space-y-6"
            >
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  施設名 <span className="text-orange-500">*</span>
                </label>
                <input
                  type="text"
                  name="facility_name"
                  required
                  placeholder="例：〇〇介護施設"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  担当者名 <span className="text-orange-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="例：山田 太郎"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  メールアドレス <span className="text-orange-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="例：info@example.com"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">電話番号</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="例：045-000-0000"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  お問い合わせ種別 <span className="text-orange-500">*</span>
                </label>
                <div className="relative">
                  <select
                    name="category"
                    required
                    defaultValue=""
                    className="w-full appearance-none border border-gray-200 rounded-lg px-4 py-3 pr-10 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition"
                  >
                    <option value="" disabled>
                      選択してください
                    </option>
                    <option value="license">ライセンスについて</option>
                    <option value="price">料金について</option>
                    <option value="introduction">導入について</option>
                    <option value="other">その他</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                    <i className="ri-arrow-down-s-line text-lg"></i>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  お問い合わせ内容 <span className="text-orange-500">*</span>
                </label>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="ご質問やご相談内容をご入力ください（500文字以内）"
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition resize-none"
                />
                <div className="mt-1 text-right text-xs text-gray-400">{charCount}/500文字</div>
              </div>

              <div className="flex justify-center">
                <Turnstile
                  sitekey="0x4AAAAAACvvXFwuMpQSvwkG"
                  onVerify={(token) => {
                    setIsVerified(true);
                    setTurnstileToken(token);
                  }}
                  onExpire={() => {
                    setIsVerified(false);
                    setTurnstileToken('');
                  }}
                  onError={() => {
                    setIsVerified(false);
                    setTurnstileToken('');
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={submitting || !isVerified}
                className={`w-full py-4 text-white font-bold text-base rounded-full transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
                  isVerified ? 'bg-orange-500 hover:bg-orange-600 hover:shadow-xl' : 'bg-gray-300'
                }`}
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    送信中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    送信する
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}